/**
 * Shared client-id core: shape check, precedence, cookie attributes. Pure (no DOM, no h3) —
 * both `resolveClientId` adapters use this instead of agreeing by parallel construction.
 *
 * UUID-checked on read and write: every carried id is attacker-supplied (query, referer,
 * cookie) → closes XSS via sinks and cookie-attribute injection via `clientIdCookieHeader`.
 *
 * Query/referer outrank cookie by decision — unifies a visitor across domains, but lets a
 * crafted link pin a bucket for the cookie's year. Accepted: bucketing key only, not auth.
 * Do not promote to anything granting access without revisiting this order.
 *
 * Cookie is `Secure`: every Solvari host is https; an http origin drops it → re-mint per request.
 */

import { hostnameOf, registrableDomain } from "../url/host.ts";

const clientIdParam = "solvariClientId";
const clientIdCookieMaxAge = 31536000;

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isSolvariId(value: string | null | undefined): value is string {
  return !!value && uuidPattern.test(value);
}

function clientIdCookieScope(host: string): string {
  return registrableDomain(host) ?? hostnameOf(host).toLowerCase();
}

// Same shape for both sinks: `document.cookie` and `Set-Cookie`.
function clientIdCookieHeader(id: string, host: string): string {
  const registrable = registrableDomain(host);
  const domain = registrable === null ? "" : `; Domain=${registrable}`;

  return `${clientIdParam}=${id}; Max-Age=${clientIdCookieMaxAge}; Path=/${domain}; Secure`;
}

// Precedence query → referer → cookie → mint, shared by both adapters.
function pickClientId({
  cookieClientId,
  queryClientId,
  refererClientId,
}: {
  cookieClientId: string | null | undefined;
  queryClientId: string | null | undefined;
  refererClientId: string | null | undefined;
}): string {
  const carried = [queryClientId, refererClientId, cookieClientId].find(
    (candidate): candidate is string => isSolvariId(candidate),
  );

  return carried ?? crypto.randomUUID();
}

export {
  clientIdCookieHeader,
  clientIdCookieScope,
  clientIdParam,
  isSolvariId,
  pickClientId,
};
